import styled, {
  css,
  FlattenSimpleInterpolation,
  Keyframes,
  keyframes,
} from 'styled-components';
import colors from '../../helpers/colors';
import mediaQuery from '../../helpers/mediaQuery';
import spacing from '../../helpers/spacing';
import Icon from '../Icon';
import TestimonialImage from './TestimonialImage';
import {
  desktopImageHeight,
  desktopImageLeftOffset,
  desktopImageTopOffset,
  desktopImageWidth,
  mobileImageHeight,
  tabletImageHeight,
} from './TestimonialImage/styles';

export const testimonialAnimationTime = 800; // This must be kept consistent with testimonialAnimationTime in TestimonialQuote\style.ts

const buttonSize = spacing(6);
const bounceDistance = spacing(1.25);
const buttonSpacing = 2;

export const TestimonialsOverflow = styled.div`
  overflow-x: hidden;

  ${mediaQuery.desktop`
    padding: ${spacing(5)} 0 calc(${spacing(5)} + ${desktopImageTopOffset}) 0 ;
    overflow: hidden;
  `}
`;

export const TestimonialsContainer = styled.div`
  position: relative;
  padding-top: ${mobileImageHeight};
  z-index: 0; //avoids child elements being at higher z indexes that other parts of the page e.g. nav overlays

  ${mediaQuery.tabletPortrait`
    padding-top: ${tabletImageHeight};
  `}

  ${mediaQuery.desktop`
    padding-top: 0;
    padding-left: ${desktopImageLeftOffset};
  `}
`;

const SlideOutToPrevious = keyframes`
  0% {
    top: var(--vertical-slide-baseline);
    left: var(--horizontal-slide-baseline);
    opacity: 1;
    visibility: visible;
  }
  60%, 100% {
    top: calc(var(--vertical-slide-baseline) - var(--vertical-slide-distance) - var(--vertical-bounce-distance));
    left: calc(var(--horizontal-slide-baseline) - var(--horizontal-slide-distance) - var(--horizontal-bounce-distance));
    opacity: var(--off-screen-opacity);
    visibility: hidden;
  }
`;

const SlideOutToNext = keyframes`
  0% {
    top: var(--vertical-slide-baseline);
    left: var(--horizontal-slide-baseline);
    opacity: 1;
    visibility: visible;
  }
  60%, 100% {
    top: calc(var(--vertical-slide-baseline) + var(--vertical-slide-distance) + var(--vertical-bounce-distance));
    left: calc(var(--horizontal-slide-baseline) + var(--horizontal-slide-distance) + var(--horizontal-bounce-distance));
    opacity: var(--off-screen-opacity);
    visibility: hidden;
  }
`;

const SlideInFromPrevious = keyframes`
  0% {
    top: calc(var(--vertical-slide-baseline) - var(--vertical-slide-distance));
    left: calc(var(--horizontal-slide-baseline) - var(--horizontal-slide-distance));
    opacity: var(--off-screen-opacity);
    visibility: hidden;
  }
  60% {
    top: calc(var(--vertical-slide-baseline) + var(--vertical-bounce-distance));
    left: calc(var(--horizontal-slide-baseline) + var(--horizontal-bounce-distance));
    opacity: 1;
    visibility: visible;
  }
  100% {
    top: var(--vertical-slide-baseline);
    left: var(--horizontal-slide-baseline);
    opacity: 1;
    visibility: visible;
  }
`;

const SlideInFromNext = keyframes`
  0% {
    top: calc(var(--vertical-slide-baseline) + var(--vertical-slide-distance));
    left: calc(var(--horizontal-slide-baseline) + var(--horizontal-slide-distance));
    opacity: var(--off-screen-opacity);
    visibility: hidden;
  }
  60% {
    top: calc(var(--vertical-slide-baseline) - var(--vertical-bounce-distance));
    left: calc(var(--horizontal-slide-baseline) - var(--horizontal-bounce-distance));
    opacity: 1;
    visibility: visible;
  }
  100% {
    top: var(--vertical-slide-baseline);
    left: var(--horizontal-slide-baseline);
    opacity: 1;
    visibility: visible;
  }
`;

type SlideDirection = 'ToPrevious' | 'ToNext' | 'FromPrevious' | 'FromNext';

const slideAnimation = (slideDirection: SlideDirection): FlattenSimpleInterpolation => {
  const animation = (animationName: Keyframes): FlattenSimpleInterpolation => css`
    animation: ${animationName} ${testimonialAnimationTime}ms forwards ease-in-out;
  `;

  switch (slideDirection) {
    case 'ToPrevious':
      return animation(SlideOutToPrevious);
    case 'ToNext':
      return animation(SlideOutToNext);
    case 'FromPrevious':
      return animation(SlideInFromPrevious);
    case 'FromNext':
      return animation(SlideInFromNext);
    default:
      throw new Error(`Invalid SlideDirection: ${slideDirection}`);
  }
};

export type TestimonialImageAnimationProps = {
  exitToPrevious?: boolean;
  exitToNext?: boolean;
  enterFromPrevious?: boolean;
  enterFromNext?: boolean;
};

export const AnimatedTestimonialImage = styled(TestimonialImage)<
  TestimonialImageAnimationProps
>`
  ${({ exitToPrevious }) => exitToPrevious && slideAnimation('ToPrevious')};
  ${({ exitToNext }) => exitToNext && slideAnimation('ToNext')};
  ${({ enterFromPrevious }) => enterFromPrevious && slideAnimation('FromPrevious')};
  ${({ enterFromNext }) => enterFromNext && slideAnimation('FromNext')};

  --vertical-slide-baseline: 0px;
  --vertical-slide-distance: 0px;
  --vertical-bounce-distance: 0px;
  --horizontal-slide-baseline: 0px;
  --horizontal-slide-distance: 100%;
  --horizontal-bounce-distance: ${bounceDistance};
  --off-screen-opacity: 1;

  ${mediaQuery.desktop`
      --vertical-slide-baseline: ${desktopImageTopOffset};
      --vertical-slide-distance: calc(${desktopImageTopOffset} + ${desktopImageHeight});
      --vertical-bounce-distance: ${bounceDistance};
      --horizontal-slide-baseline: 0px;
      --horizontal-slide-distance: 0px;
      --horizontal-bounce-distance: 0px;
      --off-screen-opacity: 0;
  `}

  visibility: visible;
`;

export const NavigationButtonsContainer = styled.div`
  position: absolute;

  top: calc(${mobileImageHeight} - ${buttonSize} / 2);
  left: calc(50% - ${buttonSize} - ${spacing(buttonSpacing / 2)});

  display: flex;
  z-index: 1; // to ensure navigation buttons are on top of the quote container

  ${mediaQuery.tabletPortrait`
    top: calc(${tabletImageHeight} - ${buttonSize} / 2);
  `}

  ${mediaQuery.desktop`
    top: calc(50% - ${buttonSize} - ${spacing(buttonSpacing / 2)});
    left: calc(${desktopImageWidth} - ${buttonSize} / 2);

    flex-direction: column;
  `}
`;

const NavigationButtonStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;

  height: ${buttonSize};
  width: ${buttonSize};

  background-color: ${colors.neutral08};
  border-radius: 90px;
  border: none;
  cursor: pointer;

  &:hover {
    box-shadow: 0 0 0 4px ${colors.core02};

    svg {
      fill: ${colors.core03};
    }
  }

  &:focus-visible {
    outline: none;
    border: 2px solid ${colors.core01};
    box-shadow: 0 0 0 4px ${colors.core02};

    svg {
      fill: ${colors.core03};
    }
  }

  &:active {
    border: 2px solid ${colors.core01};
    box-shadow: 0 0 0 10px ${colors.core02};

    svg {
      fill: ${colors.neutral01};
    }
  }
`;

const ArrowStyle = css`
  transform: rotate(270deg);

  ${mediaQuery.desktop`
    transform: none;
  `}
`;

export const PreviousButton = styled.button`
  ${NavigationButtonStyle};
`;

export const PreviousArrow = styled(Icon)`
  ${ArrowStyle};
`;

export const NextButton = styled.button`
  ${NavigationButtonStyle};
  margin-left: ${spacing(buttonSpacing)};

  ${mediaQuery.desktop`
    margin-left: 0;
    margin-top: ${spacing(buttonSpacing)};
  `}
`;

export const NextArrow = styled(Icon)`
  ${ArrowStyle};
`;
